import { supabase } from '../supabase';
import type {
  SaveAssetParams,
  ListAssetsParams,
  AssetResult,
  AssetListResult,
} from './types';

export async function saveAsset(params: SaveAssetParams): Promise<AssetResult> {
  try {
    const { type, name, files, user_fields, ai_result } = params;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { data: null, error: new Error('User not authenticated') };
    }

    const insertData = {
      owner_id: user.id,
      generator_type: type,
      name,
      files,
      preview_files: {},
      user_fields: user_fields || {},
      ai_result: ai_result || {},
      price_cents: 0,
      is_listed: false,
    };

    const { data, error } = await supabase
      .from('assets')
      .insert(insertData)
      .select()
      .single();

    if (error) {
      return { data: null, error: new Error(error.message) };
    }

    return { data, error: null };
  } catch (err) {
    return {
      data: null,
      error: err instanceof Error ? err : new Error('Unknown error occurred'),
    };
  }
}

export async function listAssets(params: ListAssetsParams): Promise<AssetListResult> {
  try {
    const { type, limit } = params;

    let query = supabase
      .from('assets')
      .select('*')
      .eq('generator_type', type)
      .order('created_at', { ascending: false });

    if (limit) {
      query = query.limit(limit);
    }

    const { data, error } = await query;

    if (error) {
      return { data: [], error: null };
    }

    return { data: data || [], error: null };
  } catch (err) {
    return {
      data: [],
      error: null,
    };
  }
}

export async function listFeaturedAssets(): Promise<AssetListResult> {
  try {
    // Only surface assets that are actually available in the marketplace (published listing).
    const { data, error } = await supabase
      .from('assets')
      .select('*, marketplace_listings!inner(id, status)')
      .eq('is_listed', true)
      .not('featured_at', 'is', null)
      .eq('marketplace_listings.status', 'published')
      .eq('marketplace_listings.bin_type', 'main')
      .order('featured_at', { ascending: false });

    if (error) {
      return loadLocalFeaturedAssets();
    }

    if (!data || data.length === 0) {
      return loadLocalFeaturedAssets();
    }

    return { data: (data as any) || [], error: null };
  } catch {
    return loadLocalFeaturedAssets();
  }
}

export async function listRecentPublicAssets(limit = 12): Promise<AssetListResult> {
  try {
    // Fetch recent public marketplace listings (published, not necessarily featured)
    const { data, error } = await supabase
      .from('assets')
      .select('*, marketplace_listings!inner(id, status)')
      .eq('is_listed', true)
      .eq('marketplace_listings.status', 'published')
      .eq('marketplace_listings.bin_type', 'main')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      return { data: [], error };
    }

    return { data: (data as any) || [], error: null };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err : new Error(String(err)) };
  }
}

async function loadLocalFeaturedAssets(): Promise<AssetListResult> {
  try {
    const response = await fetch('/assets/accessory/9f3a7c6c-851e-4e7c-b7d3-58c15ec8b64b/metadata.json');
    if (response.ok) {
      const metadata = await response.json();
      const asset: any = {
        id: metadata.id,
        owner_id: '00000000-0000-0000-0000-000000000000',
        generator_type: metadata.type,
        name: metadata.name,
        description: metadata.description || null,
        is_listed: metadata.is_listed !== false,
        price_cents: 0,
        files: metadata.files,
        preview_files: {},
        user_fields: {},
        ai_result: {},
        created_at: metadata.created_at,
        updated_at: metadata.created_at,
      };
      return { data: [asset], error: null };
    }
  } catch {
    // Error handled silently
  }

  return { data: [], error: null };
}

export async function toggleFeature(id: string, value: boolean): Promise<AssetResult> {
  try {
    const updates: Record<string, unknown> = {
      featured_at: value ? new Date().toISOString() : null,
    };

    // Ensure featured assets remain visible in the marketplace feed
    if (value) {
      updates.is_listed = true;
    }

    const { data, error } = await supabase
      .from('assets')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      return { data: null, error: new Error(error.message) };
    }

    return { data, error: null };
  } catch (err) {
    return {
      data: null,
      error: err instanceof Error ? err : new Error('Unknown error occurred'),
    };
  }
}

export async function getAssetById(id: string): Promise<AssetResult> {
  try {
    const { data, error } = await supabase
      .from('assets')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) {

      try {
        const response = await fetch(`/assets/accessory/${id}/metadata.json`);
        if (response.ok) {
          const metadata = await response.json();
          const asset: any = {
            id: metadata.id,
            owner_id: '00000000-0000-0000-0000-000000000000',
            generator_type: metadata.type,
            name: metadata.name,
            description: metadata.description || null,
            is_listed: metadata.is_listed !== false,
            price_cents: 0,
            files: metadata.files,
            preview_files: {},
            user_fields: {},
            ai_result: {},
            created_at: metadata.created_at,
            updated_at: metadata.created_at,
          };
          return { data: asset, error: null };
        }
      } catch {
        // Error handled silently
      }

      return { data: null, error: new Error(error.message) };
    }

    if (!data) {
      try {
        const response = await fetch(`/assets/accessory/${id}/metadata.json`);
        if (response.ok) {
          const metadata = await response.json();
          const asset: any = {
            id: metadata.id,
            owner_id: '00000000-0000-0000-0000-000000000000',
            generator_type: metadata.type,
            name: metadata.name,
            description: metadata.description || null,
            is_listed: metadata.is_listed !== false,
            price_cents: 0,
            files: metadata.files,
            preview_files: {},
            user_fields: {},
            ai_result: {},
            created_at: metadata.created_at,
            updated_at: metadata.created_at,
          };
          return { data: asset, error: null };
        }
      } catch {
        // Error handled silently
      }

      return { data: null, error: new Error('Asset not found') };
    }

    return { data, error: null };
  } catch (err) {
    return {
      data: null,
      error: err instanceof Error ? err : new Error('Unknown error occurred'),
    };
  }
}

export type {
  Asset,
  SaveAssetParams,
  ListAssetsParams,
  AssetResult,
  AssetListResult,
  GeneratorType,
} from './types';
